import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { getPnLMetrics } from '@/api/clarity'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { areClarityParamsValid } from '@/utils/filterParams'
import {
  PnLHierarchyModel,
  PnLMetricCardItemModel,
  PnLMetricCardModel,
  PnLTableItemModel,
} from '@/types/clarity'
import { dateToString } from '@/utils'
import { getTrendingDate } from '@/utils/date'

const pnlTableColumns = [
  {
    header: '',
    accessorKey: 'incomeExpenseTitle',
  },
  {
    header: 'Amount',
    accessorKey: 'amount',
    type: 'money',
  },
  {
    header: 'Percentage in Category',
    accessorKey: 'percentageInCategory',
    type: 'percentage',
  },
  {
    header: 'Vs Last Period',
    accessorKey: 'monthOverMonth',
    type: 'percentage',
    isTrend: true,
  },
  {
    header: 'Vs Last Year',
    accessorKey: 'yearOverYear',
    type: 'percentage',
    isTrend: true,
  },
]

export default function usePnLMetrics(
  startDate: string,
  endDate: string,
  branches: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () => areClarityParamsValid(startDate, endDate, branches),
    [startDate, endDate, branches]
  )
  const loading = !isReady || isFetching

  const [hierarchy, setHierarchy] = useState<PnLHierarchyModel[]>()
  const [metricCardData, setMetricCardData] = useState<PnLMetricCardModel>()
  const [metricCardTrendData, setMetricCardTrendData] =
    useState<PnLMetricCardModel>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { hierarchy, metricCardData } = await fetchPnLMetrics(
          branches,
          startDate,
          endDate
        )

        setHierarchy(hierarchy)
        setMetricCardData(metricCardData)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          'quarterly'
        ) as DateRange

        const { metricCardData } = await fetchPnLMetrics(
          branches,
          dateToString(trendingDate, 'from', 'yyyy-mm'),
          dateToString(trendingDate, 'to', 'yyyy-mm')
        )

        setMetricCardTrendData(metricCardData)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )
      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [fetchData, fetchTrendData, isReady])

  const refetch = useCallback(() => {
    console.log('Refetching P&L metrics data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    hierarchyData: hierarchy,
    metricCardData,
    metricCardTrendData,
    pnlTableColumns,
    loading,
    refetch,
  }
}

async function fetchPnLMetrics(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  hierarchy: PnLHierarchyModel[]
  metricCardData: PnLMetricCardModel
}> {
  try {
    const response = await getPnLMetrics(branches, startDate, endDate)

    const hierarchy: PnLHierarchyModel[] = response.hierarchy.map(
      (responseMetrics: any) => {
        return setHierarchyMetric(
          responseMetrics.id,
          responseMetrics.topLevel,
          responseMetrics?.children,
          responseMetrics
        )
      }
    )

    const metricCardData: PnLMetricCardModel = {
      storeProfits: setMetricCardMetric(
        response.metrics?.store_profits?.value,
        response.metrics?.store_profits?.mtd,
        response.metrics?.store_profits?.ytd,
        'percentage'
      ),
      netSales: setMetricCardMetric(
        response.metrics?.net_sales?.value,
        response.metrics?.net_sales?.mtd,
        response.metrics?.net_sales?.ytd,
        'money'
      ),
      rentAndOccupancy: setMetricCardMetric(
        response.metrics?.rent_occupancy?.value,
        response.metrics?.rent_occupancy?.mtd,
        response.metrics?.rent_occupancy?.ytd,
        'percentage',
        response.metrics?.rent_occupancy?.amount
      ),
      aboveUnitAG: setMetricCardMetric(
        response.metrics?.above_unit_ga?.value,
        response.metrics?.above_unit_ga?.mtd,
        response.metrics?.above_unit_ga?.ytd,
        'percentage',
        response.metrics?.above_unit_ga?.amount
      ),
      cogs: setMetricCardMetric(
        response.metrics?.cogs?.value,
        response.metrics?.cogs?.mtd,
        response.metrics?.cogs?.ytd,
        'percentage',
        response.metrics?.cogs?.amount
      ),
      storeLabor: setMetricCardMetric(
        response.metrics?.store_labor?.value,
        response.metrics?.store_labor?.mtd,
        response.metrics?.store_labor?.ytd,
        'percentage',
        response.metrics?.store_labor?.amount
      ),
      opEx: setMetricCardMetric(
        response.metrics?.op_ex?.value,
        response.metrics?.op_ex?.mtd,
        response.metrics?.op_ex?.ytd,
        'percentage',
        response.metrics?.op_ex?.amount
      ),
    }

    return {
      hierarchy,
      metricCardData,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setMetricCardMetric(
  value: number,
  mtd: number,
  ytd: number,
  metricType: 'percentage' | 'money' | 'number' | 'hour',
  amount?: number
): PnLMetricCardItemModel {
  return {
    value: formatMetric(value, 2) || 0,
    mtd: formatMetric(mtd, 2) || 0,
    ytd: formatMetric(ytd, 2) || 0,
    metricType,
    amount: amount ? formatMetric(amount, 2) : undefined,
  }
}

function setHierarchyMetric(
  id: string,
  topLevel: string,
  children?: PnLTableItemModel[],
  responseMetrics?: any
): PnLHierarchyModel {
  if (children) {
    const mapChildren = (items: PnLTableItemModel[]): PnLTableItemModel[] => {
      return items
        .map((item) => ({
          id: item.id,
          incomeExpenseTitle: item.incomeExpenseTitle,
          amount: formatMetric(item.amount, 2),
          percentageInCategory: formatMetric(item.percentageInCategory, 2),
          monthOverMonth: formatMetric(item.monthOverMonth, 2),
          yearOverYear: formatMetric(item.yearOverYear, 2),
          children: item.children ? mapChildren(item.children) : undefined,
        }))
        .sort((a, b) => {
          // Sort by amount in descending order
          const amountA = (a.amount as number) || 0
          const amountB = (b.amount as number) || 0
          return amountB - amountA
        })
    }

    children = mapChildren(children)
  }

  // Special handling for 'Above Unit' category
  if (
    responseMetrics &&
    responseMetrics.topLevel === 'Above Unit' &&
    children
  ) {
    children = processAboveUnitCategory(children)
  }

  // Add top-level summary as the first child if responseMetrics contains the data
  if (
    responseMetrics &&
    responseMetrics.amount !== undefined &&
    responseMetrics.topLevel !== 'Above Unit'
  ) {
    const topLevelSummary: PnLTableItemModel = {
      id: responseMetrics.id,
      incomeExpenseTitle:
        responseMetrics.topLevel === 'Sales'
          ? 'Net ' + responseMetrics.topLevel
          : 'Total ' + responseMetrics.topLevel,
      amount: formatMetric(responseMetrics.amount, 2),
      percentageInCategory: 100, // Top level is always 100%
      monthOverMonth: formatMetric(responseMetrics.monthOverMonth, 2),
      yearOverYear: formatMetric(responseMetrics.yearOverYear, 2),
      children: undefined,
    }

    // Insert the summary at the beginning of the children array
    children = children ? [topLevelSummary, ...children] : [topLevelSummary]
  }

  return {
    id,
    topLevel,
    children,
  }
}

function processAboveUnitCategory(
  children: PnLTableItemModel[]
): PnLTableItemModel[] {
  // Filter out 'Other Income and Expenses'
  let filteredChildren = children.filter(
    (item) =>
      !item.incomeExpenseTitle
        .toLowerCase()
        .includes('other income and expenses')
  )

  // Define the desired order for Above Unit items
  const aboveUnitOrder = [
    'EBITDA',
    'Taxes',
    'Depreciation Expense',
    'Net Income',
  ]

  // Sort children according to the specified order
  filteredChildren.sort((a, b) => {
    const aTitle = a.incomeExpenseTitle
    const bTitle = b.incomeExpenseTitle

    const aIndex = aboveUnitOrder.findIndex((order) =>
      aTitle.toLowerCase().includes(order.toLowerCase())
    )
    const bIndex = aboveUnitOrder.findIndex((order) =>
      bTitle.toLowerCase().includes(order.toLowerCase())
    )

    // If both items are in the order list, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex
    }

    // If only one is in the order list, prioritize it
    if (aIndex !== -1) return -1
    if (bIndex !== -1) return 1

    // If neither is in the order list, maintain original order
    return 0
  })

  // Rename 'Net Income' to 'Net Profit'
  return filteredChildren.map((item) => ({
    ...item,
    incomeExpenseTitle:
      item.incomeExpenseTitle === 'Net Income'
        ? 'Net Profit'
        : item.incomeExpenseTitle,
  }))
}
